// API-Client powerCOMPANY — reines fetch + TypeScript, same-origin unter /api.
// Nur die Endpunkte, die das Backend (M1) kennt: auth, users, admin/audit, mandanten, branding (BrandingContext),
// issues (IssueModal). Neue Endpunkte hier durchziehen, nicht direkt `fetch` in Seiten.
const API = '/api';

// ─── Auth ──────────────────────────────────────────────────────────────

export type AuthUser = {
  id: string;
  email: string;
  username: string | null;
  name: string;
  role: 'admin' | 'user';
  active: boolean;
  created_at: string;
  last_login_at: string | null;
};

export async function login(username: string, password: string): Promise<AuthUser> {
  const r = await fetch(`${API}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ username, password })
  });
  if (!r.ok) {
    const detail = await r.json().catch(() => ({}));
    throw new Error(detail.detail ?? `Login fehlgeschlagen (${r.status})`);
  }
  return r.json();
}

export async function logout(): Promise<void> {
  await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
}

export async function getMe(): Promise<AuthUser | null> {
  const r = await fetch(`${API}/auth/me`, { credentials: 'include' });
  if (!r.ok) return null;
  return r.json();
}


// ─── User-Verwaltung ──────────────────────────────────────────────────

export async function adminListUsers(): Promise<AuthUser[]> {
  const r = await fetch(`${API}/users`);
  if (!r.ok) throw new Error(`listUsers ${r.status}`);
  return r.json();
}

export type UserPickerItem = { id: string; username: string | null; name: string; role: string };

export async function listUsersForPicker(): Promise<UserPickerItem[]> {
  const r = await fetch(`${API}/users/picker`, { credentials: 'include' });
  if (!r.ok) return [];
  return r.json();
}

export async function adminPatchUser(
  userId: string,
  payload: {
    name?: string; email?: string; role?: string; password?: string;
    active?: boolean;
  }
): Promise<AuthUser> {
  const r = await fetch(`${API}/users/${userId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail ?? `patchUser ${r.status}`);
  }
  return r.json();
}

export async function adminDeleteUser(userId: string): Promise<void> {
  const r = await fetch(`${API}/users/${userId}`, { method: 'DELETE' });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail ?? `deleteUser ${r.status}`);
  }
}

export async function inviteUser(payload: {
  vorname: string; nachname: string; email: string; role: string;
}): Promise<{ ok: boolean; mail_sent: boolean; mail_fehler?: string; bestehendes_konto?: boolean; invite_url?: string; hinweis?: string }> {
  const r = await fetch(`${API}/users/invite`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const detail = (await r.json().catch(() => null))?.detail;
    throw new Error(detail || `inviteUser ${r.status}`);
  }
  return r.json();
}

// ─── Aktivitätsprotokoll ──────────────────────────────────────────────

export type AdminAuditEntry = {
  id: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  details: Record<string, any> | null;
  user_id: string | null;
  user_name?: string | null;
  user_username?: string | null;
  created_at: string;
};

export type AuditFilter = { limit?: number; aktion?: string; user_id?: string; seit?: string; q?: string };
export async function adminListAudit(limitOrFilter: number | AuditFilter = 200): Promise<AdminAuditEntry[]> {
  const f: AuditFilter = typeof limitOrFilter === 'number' ? { limit: limitOrFilter } : limitOrFilter;
  const qs = new URLSearchParams();
  qs.set('limit', String(f.limit ?? 200));
  if (f.aktion) qs.set('aktion', f.aktion);
  if (f.user_id) qs.set('user_id', f.user_id);
  if (f.seit) qs.set('seit', f.seit);
  if (f.q) qs.set('q', f.q);
  const r = await fetch(`${API}/admin/audit?${qs.toString()}`);
  if (!r.ok) return [];
  const data = await r.json();
  return data.entries ?? [];
}

// ─── Mandanten (Betreiber-Konsole) ─────────────────────────────────────────
export type Mandant = { slug: string; name: string; aktiv: boolean; branding: Record<string, any>; db: string; data_root: string; platte?: { status: string; gb?: number; meldung?: string } };
async function _j<T>(r: Response, was: string): Promise<T> {
  if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error((d as any).detail || `${was} ${r.status}`); }
  return r.json();
}
export type MandantNutzer = {
  id: string | null; username: string | null; email: string | null; name: string | null;
  rolle: string | null; aktiv: boolean | null;
  letzter_login: string | null; je_angemeldet: boolean;
  powerid: { vorhanden: boolean; aktiv: boolean | null; in_zugang: boolean | null; in_admin: boolean | null } | null;
  hinweise: string[];
};
export type MandantNutzerAntwort = {
  slug: string;
  powerid: { erreichbar: boolean; grund: string | null };
  gruppen: { zugang: string; zugang_da: boolean; admin: string; admin_da: boolean };
  nutzer: MandantNutzer[];
};
export async function mandantNutzer(slug: string): Promise<MandantNutzerAntwort> {
  return _j(await fetch(`${API}/mandanten/${encodeURIComponent(slug)}/nutzer`, { credentials: 'include' }), 'mandantNutzer');
}
export async function listMandanten(): Promise<{ mandanten: Mandant[]; aktuell: string | null }> {
  return _j(await fetch(`${API}/mandanten`, { credentials: 'include' }), 'listMandanten');
}
export async function createMandant(slug: string, name: string, gb = 20): Promise<Mandant> {
  return _j(await fetch(`${API}/mandanten`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slug, name, gb }) }), 'createMandant');
}
export async function updateMandant(slug: string, payload: { name?: string; aktiv?: boolean; branding?: Record<string, any> }): Promise<Mandant> {
  return _j(await fetch(`${API}/mandanten/${encodeURIComponent(slug)}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }), 'updateMandant');
}
export async function uploadMandantLogo(slug: string, file: File): Promise<Mandant> {
  const fd = new FormData(); fd.append('file', file);
  return _j(await fetch(`${API}/mandanten/${encodeURIComponent(slug)}/logo`, { method: 'POST', credentials: 'include', body: fd }), 'uploadMandantLogo');
}
export async function uploadMandantFavicon(slug: string, file: File): Promise<Mandant> {
  const fd = new FormData(); fd.append('file', file);
  return _j(await fetch(`${API}/mandanten/${encodeURIComponent(slug)}/favicon`, { method: 'POST', credentials: 'include', body: fd }), 'uploadMandantFavicon');
}
export async function deleteMandantFavicon(slug: string): Promise<Mandant> {
  return _j(await fetch(`${API}/mandanten/${encodeURIComponent(slug)}/favicon`, { method: 'DELETE', credentials: 'include' }), 'deleteMandantFavicon');
}
export async function deleteMandantLogo(slug: string): Promise<Mandant> {
  return _j(await fetch(`${API}/mandanten/${encodeURIComponent(slug)}/logo`, { method: 'DELETE', credentials: 'include' }), 'deleteMandantLogo');
}

// ─── Datei ansehen (Vorschau neben dem Chat, Seite /datei/:id) ────────────────
/** [Start, Ende] einer markierten Stelle im jeweiligen Text (UTF-16, wie JavaScript zählt). */
export type Marke = [number, number];
export type VorschauAbschnitt = {
  nr: number; art: string; inhalt: string; seite: number | null; blatt: string | null; zeile: number | null; marken: Marke[];
};
export type DateiVorschau = {
  datei: {
    id: string; pfad: string; name: string; endung: string; status: string; groesse: number;
    geaendert: string | null; geaendert_von: string | null; indexiert_am: string | null;
  };
  abschnitte: VorschauAbschnitt[]; gesamt: number; von: number; bis: number;
  /**
   * moeglich: ein Connector wartet gerade auf Abrufe. ansicht: wie das Original gezeigt wird — pdf/bild direkt,
   * umgewandelt (Word & Co. als PDF), gridcraft (xlsx), tabelle (xls/csv), null = nur herunterladen.
   */
  original: { moeglich: boolean; ansicht: 'pdf' | 'bild' | 'umgewandelt' | 'gridcraft' | 'tabelle' | null; anzeigbar: boolean; zu_gross: boolean };
};
export type TabellenBlatt = { name: string | null; zeilen: string[][]; gesamt: number; gekuerzt: boolean };
export async function dateiVorschau(id: string, q: string, um: number | null): Promise<DateiVorschau> {
  const p = new URLSearchParams({ q });
  if (um !== null) p.set('um', String(um));
  return _j(await fetch(`${API}/dateien/${encodeURIComponent(id)}?${p}`, { credentials: 'include' }), 'dateiVorschau');
}
/** Original live vom Laufwerk (über den Connector) — Anmeldung läuft über das Cookie. `pdf`: Word & Co. umgewandelt. */
export function originalUrl(id: string, art: 'anzeigen' | 'download' | 'pdf' = 'anzeigen'): string {
  const p = art === 'download' ? '?download=1' : art === 'pdf' ? '?als_pdf=1' : '';
  return `${API}/dateien/${encodeURIComponent(id)}/original${p}`;
}
/** .xls/.csv/.xlsx als Blätter und Zeilen (live vom Laufwerk gelesen). */
export async function dateiTabelle(id: string): Promise<{ blaetter: TabellenBlatt[] }> {
  return _j(await fetch(`${API}/dateien/${encodeURIComponent(id)}/tabelle`, { credentials: 'include' }), 'tabelle');
}

// ─── Fragen & Antworten (M3) ─────────────────────────────────────────────────
export type ChatKurz = { id: string; titel: string; angelegt: string; geaendert: string };
/** Quelle einer Antwort — beim Anzeigen live geprüft; ohne Recht/gelöscht nur `verfuegbar: false`. */
export type ChatQuelle = {
  n: number; verfuegbar: boolean; datei_id?: string; name?: string; pfad?: string;
  abschnitt?: number | null; seite?: number | null; geaendert_seither?: boolean;
};
export type ChatNachricht = {
  id: string; rolle: 'user' | 'assistant'; inhalt: string; angelegt: string; quellen: ChatQuelle[]; suchen?: string[];
};
export type ChatVoll = ChatKurz & { nachrichten: ChatNachricht[] };
export type ChatEreignis =
  | { typ: 'status'; text: string }
  | { typ: 'text'; text: string }
  | { typ: 'verwerfen' }
  | { typ: 'fertig'; id: string; text: string; quellen: ChatQuelle[]; suchen: string[] }
  | { typ: 'fehler'; text: string };

export async function chatsListe(): Promise<ChatKurz[]> {
  return _j(await fetch(`${API}/chats`, { credentials: 'include' }), 'chats');
}
export async function chatNeu(): Promise<ChatKurz> {
  return _j(await fetch(`${API}/chats`, { method: 'POST', credentials: 'include' }), 'chatNeu');
}
export async function chatLesen(id: string): Promise<ChatVoll> {
  return _j(await fetch(`${API}/chats/${encodeURIComponent(id)}`, { credentials: 'include' }), 'chat');
}
export async function chatLoeschen(id: string): Promise<void> {
  await _j(await fetch(`${API}/chats/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include' }), 'chatLoeschen');
}
/** Frage stellen; die Antwort kommt als Ereignisstrom (Server-Sent Events über fetch, weil POST). */
export async function chatFragen(id: string, frage: string, onEreignis: (e: ChatEreignis) => void, signal?: AbortSignal): Promise<void> {
  const r = await fetch(`${API}/chats/${encodeURIComponent(id)}/fragen`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ frage }), signal,
  });
  if (!r.ok || !r.body) {
    const d = await r.json().catch(() => ({}));
    throw new Error((d as any).detail || `Frage ${r.status}`);
  }
  const leser = r.body.getReader();
  const dec = new TextDecoder();
  let puffer = '';
  for (;;) {
    const { done, value } = await leser.read();
    if (done) break;
    puffer += dec.decode(value, { stream: true });
    let i: number;
    while ((i = puffer.indexOf('\n\n')) >= 0) {
      const block = puffer.slice(0, i);
      puffer = puffer.slice(i + 2);
      for (const z of block.split('\n')) if (z.startsWith('data: ')) onEreignis(JSON.parse(z.slice(6)));
    }
  }
}

// ─── Laufwerk (M2, nur Administratoren) ─────────────────────────────────────
/** Ein Eintrag im Änderungsverlauf. `ergebnis` leer = wird noch verarbeitet. */
export type LaufwerkAenderung = {
  id: number; erkannt: string; art: 'neu' | 'geaendert' | 'verschoben' | 'geloescht';
  pfad: string; alt_pfad: string | null; mtime: string | null;
  ergebnis: string | null; fertig: string | null; sekunden: number | null;
  /** nur, wenn die Datei noch existiert */
  datei_id: string | null;
};
export async function laufwerkAenderungen(vor?: number): Promise<LaufwerkAenderung[]> {
  const p = vor !== undefined ? `?vor=${vor}` : '';
  return _j(await fetch(`${API}/laufwerk/aenderungen${p}`, { credentials: 'include' }), 'aenderungen');
}
export type ConnectorZustand = {
  dateien?: number; wartend?: number; scan_sekunden?: number; version?: string;
  fehler?: number | string | null; letzter_scan?: string | null;
};
export type LaufwerkConnector = {
  id: string; name: string; angelegt?: string | null; zuletzt: string | null;
  zustand: ConnectorZustand; widerrufen: boolean; art?: 'connector' | 'quelle'; wurzel?: string;
};
export type LaufwerkStatus = {
  zahlen: Record<string, number>; gesamt: number;
  connectors: LaufwerkConnector[];
  fehler: { pfad: string; fehler: string | null }[];
};
const JSON_HEAD = { 'Content-Type': 'application/json' };

export async function laufwerkStatus(): Promise<LaufwerkStatus> {
  return _j(await fetch(`${API}/laufwerk/status`, { credentials: 'include' }), 'laufwerkStatus');
}
export async function laufwerkKopplungscode(): Promise<{ code: string; gueltig_minuten: number }> {
  return _j(await fetch(`${API}/laufwerk/kopplungscode`, { method: 'POST', credentials: 'include' }), 'laufwerkKopplungscode');
}
export async function laufwerkConnectorTrennen(id: string): Promise<{ ok: boolean }> {
  return _j(await fetch(`${API}/laufwerk/connectors/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include' }), 'laufwerkConnectorTrennen');
}
export async function laufwerkAusschluesse(): Promise<{ standard: string[]; eigene: string[] }> {
  return _j(await fetch(`${API}/laufwerk/ausschluesse`, { credentials: 'include' }), 'laufwerkAusschluesse');
}
export async function laufwerkAusschluesseSetzen(eigene: string[]): Promise<{ eigene: string[] }> {
  return _j(await fetch(`${API}/laufwerk/ausschluesse`, { method: 'PUT', credentials: 'include', headers: JSON_HEAD, body: JSON.stringify({ eigene }) }), 'laufwerkAusschluesseSetzen');
}
export type LaufwerkOrdner = { name: string; pfad: string; dateien: number };
/** Unterordner unter einem Präfix; `unter=""` = oberste Ebene. `pfad` endet auf `/`. */
export async function laufwerkOrdner(unter = ''): Promise<{ unter: string; ordner: LaufwerkOrdner[] }> {
  return _j(await fetch(`${API}/laufwerk/ordner?unter=${encodeURIComponent(unter)}`, { credentials: 'include' }), 'laufwerkOrdner');
}

export type Rechtegruppe = { id: string; name: string; ordner: string[]; nutzer: string[] };
export async function rechtegruppenListe(): Promise<Rechtegruppe[]> {
  return _j(await fetch(`${API}/laufwerk/rechtegruppen`, { credentials: 'include' }), 'rechtegruppenListe');
}
export async function rechtegruppeAnlegen(name: string): Promise<Rechtegruppe> {
  return _j(await fetch(`${API}/laufwerk/rechtegruppen`, { method: 'POST', credentials: 'include', headers: JSON_HEAD, body: JSON.stringify({ name }) }), 'rechtegruppeAnlegen');
}
export async function rechtegruppeUmbenennen(id: string, name: string): Promise<Rechtegruppe> {
  return _j(await fetch(`${API}/laufwerk/rechtegruppen/${encodeURIComponent(id)}`, { method: 'PUT', credentials: 'include', headers: JSON_HEAD, body: JSON.stringify({ name }) }), 'rechtegruppeUmbenennen');
}
export async function rechtegruppeLoeschen(id: string): Promise<{ ok: boolean }> {
  return _j(await fetch(`${API}/laufwerk/rechtegruppen/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include' }), 'rechtegruppeLoeschen');
}
/** Ersetzt die Ordner der Gruppe (Präfixe, `""` = ganzes Laufwerk). */
export async function rechtegruppeOrdnerSetzen(id: string, praefixe: string[]): Promise<Rechtegruppe> {
  return _j(await fetch(`${API}/laufwerk/rechtegruppen/${encodeURIComponent(id)}/ordner`, { method: 'PUT', credentials: 'include', headers: JSON_HEAD, body: JSON.stringify({ praefixe }) }), 'rechtegruppeOrdnerSetzen');
}
/** Ersetzt die Nutzer der Gruppe. */
export async function rechtegruppeNutzerSetzen(id: string, userIds: string[]): Promise<Rechtegruppe> {
  return _j(await fetch(`${API}/laufwerk/rechtegruppen/${encodeURIComponent(id)}/nutzer`, { method: 'PUT', credentials: 'include', headers: JSON_HEAD, body: JSON.stringify({ user_ids: userIds }) }), 'rechtegruppeNutzerSetzen');
}

// ─── Quellen (E32): Cloud-Speicher, WebDAV, SFTP, FTP, Postfächer — nur Mandanten-Admins ───────
export type QuellenFeld = {
  name: string; titel: string;
  typ: 'text' | 'url' | 'zahl' | 'passwort' | 'mehrzeilig' | 'ja_nein' | 'auswahl';
  geheim?: boolean; optional?: boolean; erweitert?: boolean; standard?: any; hinweis?: string;
  optionen?: [string, string][];
};
export type QuellenArt = {
  id: string; titel: string; beschreibung: string; oauth?: 'google' | 'microsoft' | 'dropbox';
  felder: QuellenFeld[]; betreiber_app: boolean | null;
};
export type QuellenArten = {
  arten: QuellenArt[]; rueckruf: string; konsole: string; anleitungen: Record<string, { titel: string; text: string }>;
};
export type Quelle = {
  id: string; art: string; art_titel: string; oauth: string | null; name: string; konfig: Record<string, any>;
  aktiv: boolean; verbunden: boolean; gesetzt: string[]; meldung: string | null; meldung_am: string | null;
  dateien: number; durchsuchbar: number; zuletzt: string | null; zustand: ConnectorZustand; angelegt: string | null;
};
export type QuellenPruefung = {
  ok: boolean; meldung?: string; anzahl?: number; mehr?: boolean; beispiele?: string[]; hostschluessel?: string | null;
};
export async function quellenArten(): Promise<QuellenArten> {
  return _j(await fetch(`${API}/quellen/arten`, { credentials: 'include' }), 'quellenArten');
}
export async function quellenListe(): Promise<Quelle[]> {
  return _j(await fetch(`${API}/quellen`, { credentials: 'include' }), 'quellenListe');
}
export async function quelleAnlegen(d: { art: string; name: string; konfig: Record<string, any>; geheim: Record<string, any> }): Promise<Quelle> {
  return _j(await fetch(`${API}/quellen`, { method: 'POST', credentials: 'include', headers: JSON_HEAD, body: JSON.stringify(d) }), 'quelleAnlegen');
}
export async function quelleAendern(id: string, d: { name?: string; konfig?: Record<string, any>; geheim?: Record<string, any>; aktiv?: boolean }): Promise<Quelle> {
  return _j(await fetch(`${API}/quellen/${encodeURIComponent(id)}`, { method: 'PUT', credentials: 'include', headers: JSON_HEAD, body: JSON.stringify(d) }), 'quelleAendern');
}
export async function quelleEntfernen(id: string): Promise<{ ok: boolean; dateien: number }> {
  return _j(await fetch(`${API}/quellen/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'include' }), 'quelleEntfernen');
}
export async function quellePruefen(d: { art: string; konfig: Record<string, any>; geheim: Record<string, any>; id?: string }): Promise<QuellenPruefung> {
  return _j(await fetch(`${API}/quellen/pruefen`, { method: 'POST', credentials: 'include', headers: JSON_HEAD, body: JSON.stringify(d) }), 'quellePruefen');
}
/** Adresse beim Anbieter (Google/Microsoft/Dropbox), dorthin weiterleiten; zurück kommt man auf /laufwerk. */
export async function quelleVerbinden(id: string): Promise<{ url: string }> {
  return _j(await fetch(`${API}/quellen/${encodeURIComponent(id)}/verbinden`, { method: 'POST', credentials: 'include' }), 'quelleVerbinden');
}

// ─── Dateibaum (A24): alle Nutzer, nach Rechten ───────
export type BaumOrdner = {
  name: string; pfad: string; dateien: number; durchsuchbar: number; groesse: number; geaendert: string | null;
  quelle: { art: string; id: string } | null;
};
export type BaumDatei = { id: string; name: string; endung: string; groesse: number; geaendert: string | null; status: string };
export type Baum = {
  pfad: string; kette: { name: string; pfad: string }[]; ordner: BaumOrdner[]; dateien: BaumDatei[]; weitere: number;
};
export async function baumLaden(pfad = ''): Promise<Baum> {
  return _j(await fetch(`${API}/baum?pfad=${encodeURIComponent(pfad)}`, { credentials: 'include' }), 'baumLaden');
}

// ─── Betreiber-Konsole: Laufwerksstatus je Mandant (nur Zahlen, keine Pfade) ───────
export type MandantLaufwerk = {
  zahlen: Record<string, number>; gesamt: number;
  connectors: Pick<LaufwerkConnector, 'id' | 'name' | 'zuletzt' | 'zustand' | 'widerrufen'>[];
};
export async function mandantLaufwerk(slug: string): Promise<MandantLaufwerk> {
  return _j(await fetch(`${API}/mandanten/${encodeURIComponent(slug)}/laufwerk`, { credentials: 'include' }), 'mandantLaufwerk');
}

// ─── Betreiber-Konsole → Cloud-Anbindungen (E34): powerCOMPANY-App je Anbieter, Secret kommt nie zurück ───
export type AnbindungApp = {
  anbieter: 'google' | 'microsoft' | 'dropbox'; titel: string; fuer: string[]; feld_id: string; feld_secret: string;
  schritte: { text: string; link: string | null }[];
  herkunft: 'konsole' | 'env' | null; client_id: string | null; geaendert: string | null; geaendert_von: string | null;
};
export type AnbindungPruefung = { ok: boolean | null; meldung: string };
export async function anbindungenListe(): Promise<{ rueckruf: string; apps: AnbindungApp[] }> {
  return _j(await fetch(`${API}/betreiber/anbindungen`, { credentials: 'include' }), 'anbindungenListe');
}
export async function anbindungSpeichern(anbieter: string, d: { client_id: string; client_secret?: string }): Promise<AnbindungApp & { pruefung: AnbindungPruefung | null }> {
  return _j(await fetch(`${API}/betreiber/anbindungen/${encodeURIComponent(anbieter)}`, { method: 'PUT', credentials: 'include', headers: JSON_HEAD, body: JSON.stringify(d) }), 'anbindungSpeichern');
}
export async function anbindungPruefen(anbieter: string, d: { client_id?: string; client_secret?: string }): Promise<AnbindungPruefung> {
  return _j(await fetch(`${API}/betreiber/anbindungen/${encodeURIComponent(anbieter)}/pruefen`, { method: 'POST', credentials: 'include', headers: JSON_HEAD, body: JSON.stringify(d) }), 'anbindungPruefen');
}
export async function anbindungEntfernen(anbieter: string): Promise<AnbindungApp & { entfernt: boolean }> {
  return _j(await fetch(`${API}/betreiber/anbindungen/${encodeURIComponent(anbieter)}`, { method: 'DELETE', credentials: 'include' }), 'anbindungEntfernen');
}
